import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Eye, EyeOff, AlertCircle, Activity } from 'lucide-react';
import { GolfButton } from './golf/golf-button';
import { Badge } from './ui/badge';
import { cn } from '@/lib/utils';

// Telemetry data interfaces
export interface TelemetryFrame {
  t: number; // timestamp in seconds
  width: number;
  height: number;
  pose?: {
    landmarks?: Array<Array<[number, number, number, number?]>>;
  };
  hands?: {
    landmarks?: Array<Array<[number, number, number, number?]>>;
  };
}

export interface TelemetryHUDProps {
  /** Video element reference for overlay positioning */
  videoRef: React.RefObject<HTMLVideoElement>;
  /** Function to get telemetry frame at specific time */
  getFrameAtTime?: (timeSec: number) => TelemetryFrame | null;
  /** Whether telemetry data is available */
  hasTelemetryData?: boolean;
  /** Whether HUD is visible */
  visible?: boolean;
  /** Callback when visibility changes */
  onVisibilityChange?: (visible: boolean) => void;
  /** Overlay opacity (0-1) */
  opacity?: number;
  /** Additional CSS classes */
  className?: string;
}

interface LayoutInfo {
  canvasWidth: number;
  canvasHeight: number;
  videoDisplayWidth: number;
  videoDisplayHeight: number;
  offsetX: number;
  offsetY: number;
  scaleX: number;
  scaleY: number;
}

export function TelemetryHUD({
  videoRef,
  getFrameAtTime,
  hasTelemetryData = false,
  visible = false,
  onVisibilityChange,
  opacity = 0.8,
  className
}: TelemetryHUDProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layoutRef = useRef<LayoutInfo | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Toggle visibility
  const toggleVisibility = useCallback(() => {
    const newVisible = !visible;
    onVisibilityChange?.(newVisible);
  }, [visible, onVisibilityChange]);

  // Compute video layout for proper overlay positioning
  const computeLayout = useCallback((): LayoutInfo | null => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas) return null;

    const videoRect = video.getBoundingClientRect();
    const videoElement = video as HTMLVideoElement;
    
    // Get actual video dimensions
    const videoWidth = videoElement.videoWidth || 1;
    const videoHeight = videoElement.videoHeight || 1;
    
    // Get display dimensions
    const displayWidth = videoRect.width;
    const displayHeight = videoRect.height;
    
    // Calculate scale to fit video in display area (object-fit: contain behavior)
    const scaleX = displayWidth / videoWidth;
    const scaleY = displayHeight / videoHeight;
    const scale = Math.min(scaleX, scaleY);
    
    // Calculate actual video display size and offset
    const videoDisplayWidth = videoWidth * scale;
    const videoDisplayHeight = videoHeight * scale;
    const offsetX = (displayWidth - videoDisplayWidth) / 2;
    const offsetY = (displayHeight - videoDisplayHeight) / 2;

    return {
      canvasWidth: displayWidth,
      canvasHeight: displayHeight,
      videoDisplayWidth,
      videoDisplayHeight,
      offsetX,
      offsetY,
      scaleX: videoDisplayWidth / videoWidth,
      scaleY: videoDisplayHeight / videoHeight
    };
  }, [videoRef]);

  // Update canvas layout
  const updateLayout = useCallback(() => {
    const layout = computeLayout();
    if (!layout) return;

    layoutRef.current = layout;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size to match video display area
    canvas.width = layout.canvasWidth;
    canvas.height = layout.canvasHeight;
    canvas.style.width = `${layout.canvasWidth}px`;
    canvas.style.height = `${layout.canvasHeight}px`;
  }, [computeLayout]);

  // Draw pose landmarks
  const drawPoseLandmarks = useCallback((
    ctx: CanvasRenderingContext2D,
    landmarks: Array<[number, number, number, number?]>,
    layout: LayoutInfo
  ) => {
    if (!landmarks || landmarks.length === 0) return;

    ctx.save();
    ctx.strokeStyle = '#00ff00';
    ctx.fillStyle = '#00ff00';
    ctx.lineWidth = 2;

    // Draw landmarks as circles
    landmarks.forEach(([x, y, z, visibility]) => {
      if (visibility !== undefined && visibility < 0.5) return; // Skip low-confidence points
      
      const canvasX = layout.offsetX + (x * layout.scaleX);
      const canvasY = layout.offsetY + (y * layout.scaleY);
      
      ctx.beginPath();
      ctx.arc(canvasX, canvasY, 3, 0, 2 * Math.PI);
      ctx.fill();
    });

    // Draw pose connections (simplified skeleton)
    const connections = [
      [11, 12], [11, 13], [13, 15], [12, 14], [14, 16], // Arms
      [11, 23], [12, 24], [23, 24], // Torso
      [23, 25], [25, 27], [24, 26], [26, 28], // Legs
    ];

    ctx.strokeStyle = '#00ff00';
    connections.forEach(([startIdx, endIdx]) => {
      if (startIdx < landmarks.length && endIdx < landmarks.length) {
        const start = landmarks[startIdx];
        const end = landmarks[endIdx];
        
        if (start && end) {
          const startX = layout.offsetX + (start[0] * layout.scaleX);
          const startY = layout.offsetY + (start[1] * layout.scaleY);
          const endX = layout.offsetX + (end[0] * layout.scaleX);
          const endY = layout.offsetY + (end[1] * layout.scaleY);
          
          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
        }
      }
    });

    ctx.restore();
  }, []);

  // Draw hand landmarks
  const drawHandLandmarks = useCallback((
    ctx: CanvasRenderingContext2D,
    landmarks: Array<[number, number, number, number?]>,
    layout: LayoutInfo
  ) => {
    if (!landmarks || landmarks.length === 0) return;

    ctx.save();
    ctx.strokeStyle = '#ff6600';
    ctx.fillStyle = '#ff6600';
    ctx.lineWidth = 1;

    // Draw hand landmarks as smaller circles
    landmarks.forEach(([x, y, z, visibility]) => {
      if (visibility !== undefined && visibility < 0.5) return;
      
      const canvasX = layout.offsetX + (x * layout.scaleX);
      const canvasY = layout.offsetY + (y * layout.scaleY);
      
      ctx.beginPath();
      ctx.arc(canvasX, canvasY, 2, 0, 2 * Math.PI);
      ctx.fill();
    });

    ctx.restore();
  }, []);

  // Main drawing function
  const drawTelemetryOverlay = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const layout = layoutRef.current;
    
    if (!canvas || !video || !layout || !visible || !getFrameAtTime) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Get current video time
    const currentTime = video.currentTime;
    const frame = getFrameAtTime(currentTime);
    
    if (!frame) {
      // Draw "No telemetry data" message
      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(10, 10, 200, 40);
      ctx.fillStyle = '#666';
      ctx.font = '14px sans-serif';
      ctx.fillText('No telemetry data available', 20, 35);
      ctx.restore();
      return;
    }

    // Set global opacity
    ctx.globalAlpha = opacity;

    // Draw pose landmarks
    if (frame.pose?.landmarks) {
      frame.pose.landmarks.forEach(landmarks => {
        drawPoseLandmarks(ctx, landmarks, layout);
      });
    }

    // Draw hand landmarks
    if (frame.hands?.landmarks) {
      frame.hands.landmarks.forEach(landmarks => {
        drawHandLandmarks(ctx, landmarks, layout);
      });
    }

    // Draw telemetry info
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(10, canvas.height - 60, 180, 50);
    ctx.fillStyle = '#fff';
    ctx.font = '12px sans-serif';
    ctx.fillText(`Time: ${currentTime.toFixed(2)}s`, 20, canvas.height - 40);
    ctx.fillText(`Frame: ${frame.t.toFixed(2)}s`, 20, canvas.height - 25);
    ctx.restore();
  }, [videoRef, getFrameAtTime, visible, opacity, drawPoseLandmarks, drawHandLandmarks]);

  // Animation loop for continuous drawing
  const startDrawing = useCallback(() => {
    if (isDrawing) return;
    
    setIsDrawing(true);
    
    const draw = () => {
      drawTelemetryOverlay();
      if (isDrawing && visible) {
        animationFrameRef.current = requestAnimationFrame(draw);
      }
    };
    
    draw();
  }, [isDrawing, visible, drawTelemetryOverlay]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Set up video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      if (visible) startDrawing();
    };
    
    const handlePause = () => {
      stopDrawing();
      // Draw one frame when paused
      if (visible) drawTelemetryOverlay();
    };

    const handleSeeked = () => {
      if (visible) drawTelemetryOverlay();
    };

    const handleLoadedMetadata = () => {
      updateLayout();
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    // Initial setup
    updateLayout();
    if (visible && !video.paused) {
      startDrawing();
    }

    return () => {
      stopDrawing();
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoRef, visible, startDrawing, stopDrawing, drawTelemetryOverlay, updateLayout]);

  // Handle visibility changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (visible && !video.paused) {
      startDrawing();
    } else {
      stopDrawing();
      if (visible) {
        // Draw one frame when visible but paused
        drawTelemetryOverlay();
      }
    }
  }, [visible, startDrawing, stopDrawing, drawTelemetryOverlay]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      updateLayout();
      if (visible) drawTelemetryOverlay();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateLayout, visible, drawTelemetryOverlay]);

  return (
    <div className={cn("relative", className)}>
      {/* HUD Toggle Control */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        <Badge 
          variant={hasTelemetryData ? "default" : "secondary"}
          className="text-xs"
        >
          <Activity className="w-3 h-3 mr-1" />
          {hasTelemetryData ? 'Telemetry Available' : 'No Telemetry'}
        </Badge>
        
        <GolfButton
          variant="outline"
          size="sm"
          onClick={toggleVisibility}
          disabled={!hasTelemetryData}
          className="bg-white/90 backdrop-blur-sm border-white/20 hover:bg-white/95"
          title={visible ? 'Hide telemetry overlay' : 'Show telemetry overlay'}
        >
          {visible ? (
            <>
              <EyeOff className="w-4 h-4 mr-1" />
              Hide HUD
            </>
          ) : (
            <>
              <Eye className="w-4 h-4 mr-1" />
              Show HUD
            </>
          )}
        </GolfButton>
      </div>

      {/* Telemetry Canvas Overlay */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 pointer-events-none"
        style={{
          zIndex: 15,
          display: visible ? 'block' : 'none',
        }}
      />

      {/* No Telemetry Message */}
      {visible && !hasTelemetryData && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div className="bg-black/70 text-white px-4 py-2 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">No telemetry data available for this video</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TelemetryHUD;
